"use client";

import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { css } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { RadioTabGroup, type RadioTabGroupItem } from "@/components/radio-tab-group";
import type { RewardsSummaryRow } from "@/contexts/queries/rewards/use-rewards-summary";
import { NumberFormatter } from "@/utils/number-formatter";

import { BREAKDOWN_ORDER, CATEGORY_DISPLAY } from "../../category-display";
import type { RewardCategory } from "../../types";
import { bannerAmountStyles, bannerCardStyles, bannerLabelStyles } from "./banner-card-styles";
import { BreakdownBar, BreakdownDot, type BreakdownSegment } from "./breakdown-bar";

type Mode = "claimed" | "earned";

const LIFETIME_LABEL_ID = "rewards-lifetime-toggle-label";

interface LifetimeEarnedCardProps {
  totalEarnedUsd: number;
  totalClaimedUsd: number;
  breakdown: RewardsSummaryRow[];
}

export function LifetimeEarnedCard({
  totalEarnedUsd,
  totalClaimedUsd,
  breakdown,
}: LifetimeEarnedCardProps) {
  const t = useTranslations("rewards");
  const locale = useLocale();
  const [mode, setMode] = useState<Mode>("claimed");

  const byCategory = useMemo(() => {
    const m = new Map<RewardCategory, RewardsSummaryRow>();
    for (const row of breakdown) {
      m.set(row.category, row);
    }
    return m;
  }, [breakdown]);

  const amountFor = (row: RewardsSummaryRow | undefined) =>
    mode === "claimed" ? (row?.totalClaimedUsd ?? 0) : (row?.lifetimeEarnedUsd ?? 0);

  const segments: BreakdownSegment[] = BREAKDOWN_ORDER.map((c) => ({
    key: c,
    usd: amountFor(byCategory.get(c)),
    color: CATEGORY_DISPLAY[c].color,
  }));

  const totalUsd = mode === "claimed" ? totalClaimedUsd : totalEarnedUsd;
  const hasNoData = totalUsd === 0;

  const toggleOptions: Array<RadioTabGroupItem<Mode>> = [
    { value: "claimed", label: t("banner.toggle.claimed") },
    { value: "earned", label: t("banner.toggle.earned") },
  ];

  return (
    <div className={bannerCardStyles}>
      <div className={headerRowStyles}>
        <div id={LIFETIME_LABEL_ID} className={bannerLabelStyles}>
          {t(mode === "claimed" ? "banner.lifetimeClaimed" : "banner.lifetimeEarned")}
        </div>
        <RadioTabGroup
          className={toggleStyles}
          aria-labelledby={LIFETIME_LABEL_ID}
          value={mode}
          onChange={setMode}
          options={toggleOptions}
        />
      </div>
      <div className={bannerAmountStyles}>
        {NumberFormatter.compactHeading(totalUsd, { currency: true, locale })}
      </div>
      <div className={css({ mt: "20" })}>
        <BreakdownBar segments={segments} />
      </div>
      <div className={flex({ mt: "16", gap: "16", wrap: "wrap", rowGap: "8" })}>
        {BREAKDOWN_ORDER.map((c) => {
          const display = CATEGORY_DISPLAY[c];
          const amount = amountFor(byCategory.get(c));
          return (
            <div key={c} className={flex({ gap: "8", alignItems: "center" })}>
              <BreakdownDot color={display.color} disabled={hasNoData} />
              <span className={legendTextStyles}>{t(`banner.breakdown.${display.labelKey}`)}</span>
              <span className={legendTextStyles}>
                {NumberFormatter.compactHeading(amount, { currency: true, locale })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const headerRowStyles = flex({
  justify: "space-between",
  align: "center",
  gap: "12",
  wrap: "wrap",
  rowGap: "8",
});

const toggleStyles = css({
  flex: "[0 0 auto]",
  "& [data-part=item]": { px: "12!", py: "2!" },
  "& [data-part=item-text]": { textStyle: "highlight.sm!" },
});

const legendTextStyles = css({ textStyle: "highlight.md", color: "text.tertiary" });
